import {
  MergedScheduledPayment,
  NameOfFrequency,
  ScheduledPaymentsRequest,
  ScheduledTransactionsRequest,
  legacyToMergedScheduledPayment,
  toMergedScheduledPayment,
} from 'vopay';
import { useState } from 'react';
import { useApi } from '#shared/providers/Api';

// Shared by every screen that maps the scheduled-transactions filter form onto
// a ScheduledPaymentsRequest, so the value vocabulary lives in one place.
export function toScheduledPaymentsFilters(filters: {
  scheduleType?: string;
  transactionType?: string;
  frequency?: NameOfFrequency | 'all' | '';
  status?: string;
}): ScheduledPaymentsRequest {
  const request: ScheduledPaymentsRequest = {};

  if (filters.scheduleType === 'single' || filters.scheduleType === 'recurring') {
    request.ScheduleType = filters.scheduleType;
  }

  if (filters.transactionType === 'fund' || filters.transactionType === 'withdraw') {
    request.PaymentType = filters.transactionType === 'fund' ? 'debit' : 'credit';
  }

  if (filters.frequency && filters.frequency !== 'all') {
    request.Repeats = filters.frequency;
  }

  if (filters.status === 'in progress' || filters.status === 'cancelled' || filters.status === 'completed') {
    request.Status = filters.status;
  }

  return request;
}

// NameOfFrequency is sent in the /scheduled-payment vocabulary — the endpoint's
// validator accepts it and (as of the earthnode ID-13095 change) translates it
// to the stored legacy vocabulary so it matches legacy rows. 'custom' is the
// one value the validator rejects, so it is filtered client-side instead.
function toLegacyScheduledTransactionsParams(params: ScheduledPaymentsRequest): ScheduledTransactionsRequest {
  return {
    StartDate: params.StartDate,
    EndDate: params.EndDate,
    SearchText: params.SearchText,
    ...(params.ScheduleType ? { Frequency: params.ScheduleType } : {}),
    ...(params.PaymentType ? { ScheduleTransactionType: params.PaymentType === 'debit' ? 'fund' : 'withdraw' } : {}),
    ...(params.Repeats && params.Repeats !== 'custom' ? { NameOfFrequency: params.Repeats } : {}),
    ...(params.Status ? { ScheduledTransactionStatus: params.Status } : {}),
    ...(params.ClientAccountID ? { ClientAccountID: params.ClientAccountID } : {}),
    Limit: params.Limit,
    Offset: params.Offset,
  };
}

/**
 * Scheduled payments for the account portal, read from both sources:
 * the new /scheduled-payment endpoint (all rails, VIOScheduledPayments only)
 * and the legacy /eft|ach/scheduled-transactions endpoint, which still holds
 * schedules that predate VIOScheduledPayments (ID-13095).
 *
 * The account's country arrives as an argument (the drawer has no portal
 * ClientData provider) — it picks the legacy endpoint's rail.
 */
export default function useFetchScheduledPayments(country: string) {
  const api = useApi();
  const [fetching, setFetching] = useState(false);

  function fetchLegacyScheduledTransactions(params: ScheduledTransactionsRequest) {
    return country === 'US' ? api.fetchACHScheduledTransactions(params) : api.fetchScheduledTransactions(params);
  }

  // The legacy endpoint merges old + new tables server-side and tags rows that
  // came from the new table with TransactionType — those are duplicates of what
  // /scheduled-payment already returned, so only the untagged rows are kept
  // (a page can therefore hold fewer legacy rows than its Limit).
  // The legacy source supplements /scheduled-payment, so its failure must not
  // take down the whole list: it is logged and degrades to an empty set.
  async function getLegacyScheduledPayments(
    params: ScheduledPaymentsRequest,
  ): Promise<{ payments: MergedScheduledPayment[]; numTotalRecords: number }> {
    try {
      const legacyRes = await fetchLegacyScheduledTransactions(toLegacyScheduledTransactionsParams(params));
      if (!legacyRes.Success) throw new Error(legacyRes.ErrorMessage);

      // The frequency filter also runs client-side (on the normalized Repeats):
      // 'custom' is never sent to the legacy endpoint (its validator rejects
      // it), so this is where a 'custom' filter takes effect.
      const payments = legacyRes.ScheduledTransactions.filter((transaction) => !transaction.TransactionType)
        .map((transaction) => legacyToMergedScheduledPayment(transaction, country))
        .filter((payment) => !params.Repeats || payment.Repeats === params.Repeats);

      return { payments, numTotalRecords: legacyRes.PaginationMetaData?.TotalRecords ?? payments.length };
    } catch (error) {
      console.error(error);

      return { payments: [], numTotalRecords: 0 };
    }
  }

  // Both sources are queried with the same Limit/Offset and paginate
  // independently: a page can hold up to 2×Limit rows and the date sort only
  // holds within a page.
  async function fetchScheduledPayments(params: ScheduledPaymentsRequest) {
    try {
      setFetching(true);

      const [newRes, legacy] = await Promise.all([
        api.fetchScheduledPayments(params),
        getLegacyScheduledPayments(params),
      ]);

      if (!newRes.Success) throw new Error(newRes.ErrorMessage);

      const scheduledPayments = [...newRes.ScheduledPayments.map(toMergedScheduledPayment), ...legacy.payments].sort(
        (a, b) => b.StartDateTime.localeCompare(a.StartDateTime),
      );

      // The page count follows whichever source has more records.
      const numTotalRecords = Math.max(
        newRes.PaginationMetaData?.TotalRecords ?? newRes.ScheduledPayments.length,
        legacy.numTotalRecords,
      );

      return { scheduledPayments, numTotalRecords };
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      setFetching(false);
    }
  }

  return { fetching, fetchScheduledPayments };
}
